<script setup lang="ts">
import { getCurrentInstance } from "vue";
import type { AccordionItemProps } from "./accordion.types";
import { useAccordionItem } from "./useAccordionItem";

const props = withDefaults(defineProps<AccordionItemProps>(), {
  size: "sm",
  open: false,
  disabled: false,
  variant: "transparent",
});

const { onSummaryClick, isOpen, classes, innerCls } = useAccordionItem({ props });

const uid = getCurrentInstance()!.uid;
const triggerId = `accordion-trigger-${uid}`;
const contentId = `accordion-content-${uid}`;
</script>

<template>
  <e-primitive as="div" :inner-class="innerCls">
    <details open part="base" :class="classes.details.value">
      <summary
        :id="triggerId"
        part="summary"
        :class="classes.summary.value"
        :aria-expanded="isOpen"
        :aria-controls="contentId"
        @click.prevent="onSummaryClick"
      >
        <slot name="leading" />
        <span class="min-w-0 flex-1">
          <slot name="header" />
        </span>
        <e-icon exportparts="base:indicator-icon" name="chevron-up" :class="classes.indicator.value" size="sm" />
      </summary>
      <div
        :id="contentId"
        part="content"
        class="content-wrapper"
        role="region"
        :aria-labelledby="triggerId"
        :data-open="isOpen"
        :inert="!isOpen"
      >
        <div class="content-collapsible">
          <div :class="classes.content.value">
            <slot name="content" />
          </div>
        </div>
      </div>
    </details>
  </e-primitive>
</template>

<style>
:host {
  display: contents;
}

.content-wrapper {
  display: grid;
  grid-template-rows: 0fr;
  overflow: hidden;
  transition: grid-template-rows 200ms ease;
}

.content-wrapper[data-open="true"] {
  grid-template-rows: 1fr;
}

.content-collapsible {
  min-height: 0;
  overflow: hidden;
}
</style>
